import { useState } from "react";
import { toast } from "sonner";
import { inspectionApi } from "./api";
import { TierBadge } from "./TierBadge";

export const InspectionSummary = ({ insp, onJump, onCompleted }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const { progress: prog, classification: cls } = insp;
  const done = insp.status === "completed";
  const finalCls = insp.classification || cls;

  const roomIdxByLabel = (label) => insp.rooms.findIndex((r) => r.label === label);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await inspectionApi.complete(insp.inspection_id, { override_reason: reason });
      toast.success(`Inspection locked · ${res.classification.label}`);
      onCompleted();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runScan = async () => {
    setBusy(true);
    try {
      const res = await inspectionApi.hunterScan(insp.inspection_id);
      setScan(res);
      toast.success(`Hunter Scan complete · ${res.counts?.total ?? 0} findings`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="insp-room" data-testid="insp-summary">
      <h2 className="insp-room__title">Turn-Tier Result</h2>
      <TierBadge cls={finalCls} />
      <p className="insp-sum__text" data-testid="insp-summary-text">{finalCls.summary}</p>
      <div className="insp-sum__stats">
        <div><b data-testid="insp-sum-score">{finalCls.score}</b><span>Scope score</span></div>
        <div><b>{prog.photo_count}</b><span>Photos vaulted</span></div>
        <div><b>{prog.required_shots_done}/{prog.required_shots}</b><span>Required shots</span></div>
        <div><b>{prog.items_done}/{prog.items}</b><span>Items scored</span></div>
      </div>
      {finalCls.drivers?.length > 0 && (
        <ul className="insp-list" data-testid="insp-drivers">{finalCls.drivers.map((d) => <li key={d}>{d}</li>)}</ul>
      )}
      {finalCls.structural_hold && (
        <div className="insp-alert" data-testid="insp-structural-hold">
          <b>Structural guardrail · CEO approval call required</b>
          <div>The project clock pauses on completion. No trade mobilizes until the CEO authorizes the variance.</div>
          {finalCls.structural_items.map((f) => <div key={f.room + f.item}>{f.room}: {f.item} ({f.condition})</div>)}
        </div>
      )}
      {finalCls.two_person_required?.length > 0 && (
        <div className="insp-card" data-testid="insp-two-person">
          <div className="insp-card__title">OSHA 30 · two-person trade team required</div>
          {finalCls.two_person_required.map((f) => <div key={f.room + f.item} className="insp-spec">{f.room}: {f.item}</div>)}
        </div>
      )}
      {finalCls.precondition_hold && (
        <div className="insp-alert" data-testid="insp-clear-unit-hold">
          <b>Clear Unit hold · do not mobilize</b>
          <div>MSA §5(c): the property's licensed maintenance team must remove all major appliances and cap gas, water and electric before work begins. Moovin Forward never disconnects appliances.</div>
          {finalCls.precondition_failures.map((f) => <div key={f.room + f.item}>{f.room}: {f.item}{f.note ? ` (${f.note})` : ""}</div>)}
        </div>
      )}
      {finalCls.life_safety_flags?.length > 0 && (
        <div className="insp-alert" data-testid="insp-life-safety">
          <b>NSPIRE hold · correct before lease</b>
          {finalCls.life_safety_flags.map((f) => <div key={f.room + f.item}>{f.room}: {f.item} ({f.condition}){f.responsible && f.responsible !== "Moovin Forward" ? ` · ${f.responsible}` : ""}</div>)}
        </div>
      )}
      {insp.deficiency_schedule?.length > 0 && (
        <div className="insp-card" data-testid="insp-deficiency-schedule">
          <div className="insp-card__title">NSPIRE correction schedule</div>
          {insp.deficiency_schedule.map((d) => (
            <div key={d.room + d.item} className={`insp-def insp-def--${d.class}`}>
              <b>{d.class_label}</b> · {d.room}: {d.item}{d.responsible && d.responsible !== "Moovin Forward" ? " · Client scope" : ""}
              <span>{d.class === "life_threatening" ? "Correct within 24 hours" : `Correct within ${Math.round(d.window_hours / 24)} days`}{done ? ` · by ${new Date(d.due_at).toLocaleDateString()}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {finalCls.client_responsibility_items?.length > 0 && (
        <div className="insp-card" data-testid="insp-client-scope">
          <div className="insp-card__title">Client responsibility · documented, not in our scope or price</div>
          {finalCls.client_responsibility_items.map((c) => <div key={c.room + c.item} className="insp-spec">{c.room}: {c.item} <span>{c.condition} · {c.responsible}</span></div>)}
        </div>
      )}
      {insp.auto_spec?.length > 0 && (
        <div className="insp-card" data-testid="insp-auto-spec">
          <div className="insp-card__title">Auto-assigned hardware spec</div>
          {insp.auto_spec.map((x) => <div key={x.room + x.key} className="insp-spec"><b>{x.qty}×</b> {x.spec} <span>{x.room} · {x.basis}</span></div>)}
        </div>
      )}
      {!done && prog.missing_required_shots.length > 0 && (
        <div className="insp-missing" data-testid="insp-missing">
          <b>Missing required shots ({prog.missing_required_shots.length})</b>
          {prog.missing_required_shots.slice(0, 12).map((m) => (
            <button key={m.room + m.shot} type="button" className="insp-missing__row" onClick={() => onJump(roomIdxByLabel(m.room))}>
              {m.room} · {m.shot} →
            </button>
          ))}
        </div>
      )}
      {done ? (
        <>
          <a className="insp-btn insp-btn--primary insp-btn--link" href={`/render-hub/${insp.inspection_id}`} data-testid="insp-open-render-hub">Open Render Hub · before/after + procurement →</a>
          <div className="insp-locked" data-testid="insp-locked">Locked {new Date(insp.completed_at).toLocaleString()} · written to project, NSPIRE record and evidence vault.</div>
          {scan || insp.hunter_scan_id ? (
            <div className="insp-locked" data-testid="insp-hunter-result">
              Hunter Scan {scan?.scan_id || insp.hunter_scan_id}
              {scan && <> · {scan.counts?.total} findings · {scan.counts?.life_safety} life-safety · {scan.estimated_labor_hours} labor hrs · Packet 1 {scan.packet_1?.packet_id || "drafted"}</>}
            </div>
          ) : (
            <button type="button" className="insp-btn insp-btn--primary" disabled={busy} onClick={runScan} data-testid="insp-hunter-scan-btn">
              {busy ? "Scanning photos…" : "Run Hunter Scan → defect ledger + Packet 1"}
            </button>
          )}
        </>
      ) : (
        <>
          {!prog.complete && (
            <textarea className="insp-note insp-note--area" placeholder="Not finished? Give the reason you're closing it early (e.g. occupied bedroom, no access to mechanical)."
                      value={reason} onChange={(e) => setReason(e.target.value)} data-testid="insp-override-reason" />
          )}
          <button type="button" className="insp-btn insp-btn--primary" disabled={busy || (!prog.complete && reason.trim().length < 8)}
                  onClick={submit} data-testid="insp-complete-btn">
            {busy ? "Locking…" : "Complete & lock inspection"}
          </button>
        </>
      )}
    </section>
  );
};
